import * as THREE from 'three';
import type { FloraDefinition } from '@/flora/FloraDefinition';
import { createFloraInstance } from '@/flora/FloraInstance';
import { FloraFactory } from '@/flora/FloraFactory';
import { resolveSeasonalAppearance } from '@/flora/FloraSeasonModel';
import type { FloraResolver } from '@/localization/FloraResolver';
import { RenderQuality } from './RenderQuality';
import { WebGLRendererAdapter } from './Renderer';
import type { Renderer } from './Renderer';

/**
 * A turntable for one species: the real flora mesh, at maturity, on a metre grid, beside a
 * SketchUp-style cutout figure — the "how big is this actually" view that a silhouette and a number
 * can only gesture at.
 *
 * It builds through the same {@link FloraFactory} and {@link resolveSeasonalAppearance} the scene
 * uses, so what the card shows is the plant the site would get, not a picture of one. That is the
 * whole point of putting a live viewport in a catalog card rather than pre-rendering thumbnails: a
 * turntable that agreed with the catalog on the day it was baked and disagreed after the next
 * geometry change would be worse than no preview at all.
 *
 * It lives in `src/rendering/` because it owns a renderer, and the spec (§4) keeps concrete
 * renderers behind this boundary so a future WebGPU backend is a one-file change. The UI gets a
 * canvas and four methods.
 *
 * ## Costs, and what is done about them
 *
 * This is a *second* WebGL context sitting next to the main viewport, so it is deliberately frugal:
 * it draws on demand rather than in a standing loop, the turntable spins for exactly one revolution
 * on open and then stops, and {@link stop} parks it whenever the card is closed. Idle, it costs
 * nothing but its context. The renderer is built lazily on the first {@link show} and reused, since
 * contexts are a scarce per-page resource and churning one per plant would eventually lose the
 * oldest — which would be the main viewport.
 */

/** The reference figure: an average adult, the same one the 2D fallback drawing uses. */
export const PERSON_HEIGHT_METERS = 1.8;
/** Shoulder-to-shoulder width of the cutout, in metres. */
const PERSON_WIDTH_METERS = 0.62;
/** Vertical field of view, in degrees. Narrow enough that a tall tree does not fan outward. */
const FIELD_OF_VIEW = 34;
/** One full turn on open, then the turntable rests. */
const SPIN_DEGREES_PER_SECOND = 26;
/** Instances are seeded so a species looks the same every time its card is opened. */
const PREVIEW_SEED = 20260812;

export interface SubjectSize {
  heightMeters: number;
  spreadMeters: number;
}

export interface Framing {
  /** Camera distance from the orbit target, in metres. */
  distance: number;
  /** Height of the orbit target above the ground, in metres. */
  targetY: number;
  /** Where the figure stands, in metres to the side of the plant's axis. */
  personX: number;
  /** Ground grid spacing in metres. */
  gridSpacing: number;
  /** Half-width of the ground plane, in metres. */
  groundRadius: number;
}

/**
 * Choose a grid that reads as a ruler rather than as graph paper: roughly six to twelve squares
 * across whatever is being looked at, snapped to a spacing a person would actually measure in.
 */
export function gridSpacingFor(spanMeters: number): number {
  // Half a metre is the finest square offered: the caption states the spacing in the reader's units,
  // and a quarter-metre grid prints as "0.3 m", which is a worse lie than a coarser grid.
  const candidates = [0.5, 1, 2, 5, 10];
  const target = spanMeters / 8;
  let best = candidates[0] ?? 1;
  for (const candidate of candidates) {
    if (candidate <= target) best = candidate;
  }
  return best;
}

/**
 * Frame the plant and the figure together. Both the vertical and the horizontal extent are solved
 * for and the looser one wins, so a 15 m maple pulls back for its height while a 0.3 m groundcover
 * pulls back for the width of the person standing next to it — which is the case where a
 * height-only fit would put the camera inside the shrubbery.
 */
export function frameSubject(size: SubjectSize, aspect: number): Framing {
  const plantHeight = Math.max(0.2, size.heightMeters);
  const plantSpread = Math.max(0.2, size.spreadMeters);
  const worldHeight = Math.max(plantHeight, PERSON_HEIGHT_METERS) * 1.14;

  // The figure stands clear of the canopy, never closer than a stride from the trunk.
  const personX = Math.max(1.1, plantSpread / 2 + 0.85);
  const halfWidth = personX + PERSON_WIDTH_METERS;

  const vFov = (FIELD_OF_VIEW * Math.PI) / 180;
  const safeAspect = Math.max(0.3, aspect);
  const hFov = 2 * Math.atan(Math.tan(vFov / 2) * safeAspect);

  const forHeight = worldHeight / 2 / Math.tan(vFov / 2);
  const forWidth = halfWidth / Math.tan(hFov / 2);
  const distance = Math.max(forHeight, forWidth) * 1.18;

  const span = Math.max(worldHeight, halfWidth * 2);
  return {
    distance,
    targetY: worldHeight * 0.42,
    personX,
    gridSpacing: gridSpacingFor(span),
    groundRadius: Math.max(span * 0.9, personX + 2),
  };
}

/** Orbit state, in the same terms the main camera uses: azimuth, elevation, distance. */
interface Orbit {
  azimuth: number;
  elevation: number;
  distance: number;
}

export interface FloraPreviewOptions {
  quality?: RenderQuality;
  /** Injected so a test can supply a renderer without a GL context. */
  createRenderer?: () => Renderer;
}

export class FloraPreview {
  /** The canvas to place in the page. Null when this browser could not give us a context. */
  readonly canvas: HTMLCanvasElement | null;

  private readonly renderer: Renderer | null;
  private readonly scene = new THREE.Scene();
  private readonly camera: THREE.PerspectiveCamera;
  private readonly factory = new FloraFactory({ detail: 'high' });
  private readonly subject = new THREE.Group();
  private readonly sun: THREE.DirectionalLight;
  private readonly person: THREE.Mesh;
  private readonly ground: THREE.Mesh;
  private grid: THREE.GridHelper | null = null;
  private backgroundTexture: THREE.Texture | null = null;
  private theme: 'light' | 'dark' = 'light';

  private plant: THREE.Object3D | null = null;
  private framing: Framing = frameSubject({ heightMeters: 3, spreadMeters: 2 }, 4 / 3);
  private orbit: Orbit = { azimuth: -0.62, elevation: 0.2, distance: 10 };
  private width = 320;
  private height = 240;

  private frameHandle: number | null = null;
  private lastFrameMs = 0;
  /** Degrees of turntable left to spin. Zeroed by the first drag. */
  private spinRemaining = 0;
  private dragPointer: number | null = null;
  private lastX = 0;
  private lastY = 0;

  private readonly onPointerDown = (event: PointerEvent): void => {
    if (event.button !== 0 || !this.canvas) return;
    this.dragPointer = event.pointerId;
    this.lastX = event.clientX;
    this.lastY = event.clientY;
    this.spinRemaining = 0;
    this.canvas.setPointerCapture(event.pointerId);
    this.canvas.classList.add('is-dragging');
    event.preventDefault();
  };

  private readonly onPointerMove = (event: PointerEvent): void => {
    if (this.dragPointer !== event.pointerId) return;
    const dx = event.clientX - this.lastX;
    const dy = event.clientY - this.lastY;
    this.lastX = event.clientX;
    this.lastY = event.clientY;

    this.orbit.azimuth -= dx * 0.008;
    // Clamped just above the ground and just below straight down: orbiting under the site would
    // show the underside of a ground plane, which reads as a rendering fault rather than a view.
    this.orbit.elevation = clamp(this.orbit.elevation + dy * 0.006, -0.02, 1.35);
    this.requestFrame();
  };

  private readonly onPointerUp = (event: PointerEvent): void => {
    if (this.dragPointer !== event.pointerId) return;
    this.dragPointer = null;
    this.canvas?.classList.remove('is-dragging');
    if (this.canvas?.hasPointerCapture(event.pointerId)) {
      this.canvas.releasePointerCapture(event.pointerId);
    }
  };

  private readonly onWheel = (event: WheelEvent): void => {
    // Non-passive: without this the card's scroll container eats the gesture and the page moves
    // instead of the camera.
    event.preventDefault();
    this.spinRemaining = 0;
    const factor = Math.exp(event.deltaY * 0.0012);
    this.orbit.distance = clamp(
      this.orbit.distance * factor,
      this.framing.distance * 0.3,
      this.framing.distance * 2.6,
    );
    this.requestFrame();
  };

  private readonly onDoubleClick = (): void => {
    this.resetView();
  };

  constructor(options: FloraPreviewOptions = {}) {
    this.camera = new THREE.PerspectiveCamera(FIELD_OF_VIEW, 4 / 3, 0.05, 800);

    let renderer: Renderer | null = null;
    try {
      renderer =
        options.createRenderer?.() ??
        new WebGLRendererAdapter({ quality: options.quality ?? RenderQuality.Medium });
    } catch {
      // No WebGL here (headless, a blocked context, or one context too many). The card falls back
      // to its drawn scale figure rather than showing a dead rectangle.
      renderer = null;
    }
    this.renderer = renderer;
    this.canvas = renderer?.domElement ?? null;

    this.sun = new THREE.DirectionalLight(0xfff4e0, 2.4);
    this.ground = this.buildGround();
    this.person = this.buildPerson();
    this.scene.add(this.subject);
    this.buildStudio();

    if (this.canvas) {
      this.canvas.className = 'lg-card__canvas';
      this.canvas.addEventListener('pointerdown', this.onPointerDown);
      this.canvas.addEventListener('pointermove', this.onPointerMove);
      this.canvas.addEventListener('pointerup', this.onPointerUp);
      this.canvas.addEventListener('pointercancel', this.onPointerUp);
      this.canvas.addEventListener('wheel', this.onWheel, { passive: false });
      this.canvas.addEventListener('dblclick', this.onDoubleClick);
    }
  }

  /** False when this browser gave us no context; the caller should show its fallback instead. */
  get available(): boolean {
    return this.renderer !== null && this.canvas !== null;
  }

  /* -------------------------------------- Studio --------------------------------------- */

  private buildStudio(): void {
    this.sun.position.set(6, 12, 8);
    this.sun.castShadow = true;
    // A tight shadow frustum: the subject is metres across, not the eighty metres the site rig is
    // configured for, and reusing that would spend a 2048 map on mostly empty ground.
    this.sun.shadow.mapSize.set(1024, 1024);
    this.sun.shadow.camera.near = 0.5;
    this.sun.shadow.camera.far = 120;
    this.sun.shadow.bias = -0.0012;
    this.sun.shadow.normalBias = 0.02;

    this.scene.add(
      this.sun,
      this.sun.target,
      new THREE.HemisphereLight(0xcfe2ff, 0x54452f, 0.75),
      new THREE.AmbientLight(0xffffff, 0.22),
    );
    this.subject.add(this.ground, this.person);
    this.setTheme('light');
  }

  private buildGround(): THREE.Mesh {
    const geometry = new THREE.CircleGeometry(1, 64);
    geometry.rotateX(-Math.PI / 2);
    const material = new THREE.MeshStandardMaterial({
      color: 0x6f7a6a,
      roughness: 0.96,
      metalness: 0,
    });
    const mesh = new THREE.Mesh(geometry, material);
    mesh.receiveShadow = true;
    mesh.name = 'preview-ground';
    return mesh;
  }

  /**
   * The scale figure: a flat cutout that turns to face the camera, which is what SketchUp's 2D
   * people do and for the same reason — it reads as a *scale annotation* rather than as a person
   * who has been modelled badly, and it stays legible from every angle of the orbit.
   */
  private buildPerson(): THREE.Mesh {
    const geometry = new THREE.PlaneGeometry(PERSON_WIDTH_METERS, PERSON_HEIGHT_METERS);
    geometry.translate(0, PERSON_HEIGHT_METERS / 2, 0);
    const material = new THREE.MeshBasicMaterial({
      map: personTexture(),
      transparent: true,
      opacity: 0.62,
      // Unlit and depth-write-free: an annotation should not take the scene's light, nor punch a
      // transparent hole in whatever is drawn behind it.
      depthWrite: false,
      side: THREE.DoubleSide,
      toneMapped: false,
      // The cutout casts a real shadow, which is what stops a flat figure from reading as floating
      // above the grid. The shadow pass tests this threshold against the texture's own alpha, so
      // the silhouette — not the quad — is what lands on the ground.
      alphaTest: 0.45,
    });
    const mesh = new THREE.Mesh(geometry, material);
    mesh.name = 'preview-figure';
    mesh.castShadow = true;
    mesh.renderOrder = 2;
    return mesh;
  }

  /**
   * Match the card's theme. Not just the backdrop: the annotations — the figure and the grid — are
   * ink on the ground, so they have to invert with it or they disappear into a dark studio.
   */
  setTheme(theme: 'light' | 'dark'): void {
    this.theme = theme;
    this.backgroundTexture?.dispose();
    this.backgroundTexture = gradientTexture(
      theme === 'dark' ? ['#2a323d', '#171c23'] : ['#e8edf1', '#cdd6dd'],
    );
    this.scene.background = this.backgroundTexture;

    const groundMaterial = this.ground.material as THREE.MeshStandardMaterial;
    groundMaterial.color.set(theme === 'dark' ? 0x424b45 : 0x8d9a86);

    // The cutout's texture is white, so its colour is set here rather than baked at draw time.
    const personMaterial = this.person.material as THREE.MeshBasicMaterial;
    personMaterial.color.set(theme === 'dark' ? 0xd8e2ee : 0x2b3542);
    personMaterial.opacity = theme === 'dark' ? 0.5 : 0.62;

    this.applyGridTheme();
    this.requestFrame();
  }

  private applyGridTheme(): void {
    if (!this.grid) return;
    const { material } = this.grid;
    material.color.set(this.theme === 'dark' ? 0xdfe8f2 : 0x1b2430);
    material.opacity = this.theme === 'dark' ? 0.22 : 0.4;
  }

  /* --------------------------------------- Subject -------------------------------------- */

  /**
   * Put a species on the turntable, grown, in the season the rest of the card is showing. Re-framed
   * from scratch each time: the catalog runs from a 0.1 m groundcover to a 21 m shade tree, and one
   * camera cannot serve both.
   */
  show(definition: FloraDefinition, dayOfYear: number, resolver?: FloraResolver): void {
    if (!this.available) return;
    this.clearPlant();

    const instance = createFloraInstance(definition.id, {
      maturity: 1,
      visualSeed: PREVIEW_SEED,
    });
    const plant = this.factory.build(definition, instance);
    // Through the site's resolver when there is one, so the record card's turntable shows the plant
    // in the state the canvas behind it is drawing — a card claiming full bloom over a scene showing
    // bare wood is worse than no card.
    this.factory.applySeasonalState(
      plant,
      resolver
        ? resolver.getPlantState(definition, dayOfYear)
        : resolveSeasonalAppearance(definition.seasons, dayOfYear),
    );
    plant.traverse((node) => {
      if ((node as Partial<THREE.Mesh>).isMesh) {
        node.castShadow = true;
        node.receiveShadow = true;
      }
    });
    this.subject.add(plant);
    this.plant = plant;

    this.framing = frameSubject(
      {
        heightMeters: definition.growth.matureHeightMeters.typical,
        spreadMeters: definition.growth.matureSpreadMeters.typical,
      },
      this.width / Math.max(1, this.height),
    );
    this.applyFraming();
    this.resetView();
    // One turn, so the card announces itself as three-dimensional without becoming an animation.
    this.spinRemaining = 360;
    this.start();
  }

  private applyFraming(): void {
    const { gridSpacing, groundRadius, personX } = this.framing;

    this.ground.scale.setScalar(groundRadius);
    this.person.position.set(personX, 0, 0);

    if (this.grid) {
      this.subject.remove(this.grid);
      this.grid.geometry.dispose();
      disposeMaterial(this.grid.material);
    }
    // The grid is inscribed in the ground disc — a square that fits inside the circle — so its
    // corners never hang off the edge of the ground they are measuring.
    const divisions = Math.max(4, Math.floor((groundRadius * Math.SQRT2) / gridSpacing));
    const extent = divisions * gridSpacing;
    // Built white so `applyGridTheme` can tint it: the helper bakes its colours into vertex
    // attributes, which the material's colour then multiplies.
    this.grid = new THREE.GridHelper(extent, divisions, 0xffffff, 0xffffff);
    const gridMaterial = this.grid.material as THREE.Material;
    gridMaterial.transparent = true;
    gridMaterial.depthWrite = false;
    this.applyGridTheme();
    // Just clear of the ground disc, or the two z-fight into a moiré as the camera orbits.
    this.grid.position.y = 0.004;
    this.grid.renderOrder = 1;
    this.subject.add(this.grid);

    // Keep the shadow frustum wrapped around the subject at whatever size it turned out to be.
    const reach = Math.max(groundRadius, this.framing.targetY * 2.4);
    const shadowCamera = this.sun.shadow.camera;
    shadowCamera.left = -reach;
    shadowCamera.right = reach;
    shadowCamera.top = reach;
    shadowCamera.bottom = -reach;
    shadowCamera.far = reach * 6;
    shadowCamera.updateProjectionMatrix();
    this.sun.position.set(reach * 0.6, reach * 1.3, reach * 0.8);
  }

  /** Back to the opening three-quarter view. */
  resetView(): void {
    this.orbit = { azimuth: -0.62, elevation: 0.2, distance: this.framing.distance };
    this.requestFrame();
  }

  private clearPlant(): void {
    if (!this.plant) return;
    this.subject.remove(this.plant);
    this.factory.dispose(this.plant);
    this.plant = null;
  }

  /* ------------------------------------- Presentation ------------------------------------ */

  setSize(width: number, height: number): void {
    const w = Math.max(1, Math.round(width));
    const h = Math.max(1, Math.round(height));
    if (w === this.width && h === this.height) return;
    this.width = w;
    this.height = h;
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.renderer?.setSize(w, h);
    this.requestFrame();
  }

  /** Resume drawing. Cheap to call repeatedly. */
  start(): void {
    if (!this.available || this.frameHandle !== null) return;
    this.lastFrameMs = now();
    this.frameHandle = requestAnimationFrame(this.tick);
  }

  /** Park the viewport — the card calls this the moment it closes. */
  stop(): void {
    if (this.frameHandle === null) return;
    cancelAnimationFrame(this.frameHandle);
    this.frameHandle = null;
    this.spinRemaining = 0;
  }

  private readonly tick = (): void => {
    this.frameHandle = null;
    const time = now();
    const deltaSeconds = Math.min(0.1, (time - this.lastFrameMs) / 1000);
    this.lastFrameMs = time;

    if (this.spinRemaining > 0 && this.dragPointer === null) {
      const step = SPIN_DEGREES_PER_SECOND * deltaSeconds;
      this.spinRemaining -= step;
      this.orbit.azimuth += (step * Math.PI) / 180;
    }

    this.draw();

    // Keep the loop alive only while something is actually moving.
    if (this.spinRemaining > 0 || this.dragPointer !== null) {
      this.frameHandle = requestAnimationFrame(this.tick);
    }
  };

  /** Draw one frame now, without starting a loop. */
  private requestFrame(): void {
    if (!this.available) return;
    if (this.frameHandle !== null) return;
    if (this.dragPointer !== null || this.spinRemaining > 0) {
      this.start();
      return;
    }
    this.draw();
  }

  private draw(): void {
    if (!this.renderer) return;
    const { targetY } = this.framing;
    const { azimuth, elevation, distance } = this.orbit;
    const horizontal = Math.cos(elevation) * distance;

    this.camera.position.set(
      Math.sin(azimuth) * horizontal,
      targetY + Math.sin(elevation) * distance,
      Math.cos(azimuth) * horizontal,
    );
    this.camera.lookAt(0, targetY, 0);

    // The cutout turns on its own axis to face the camera — flat to the view, upright on the ground.
    this.person.rotation.y = Math.atan2(
      this.camera.position.x - this.person.position.x,
      this.camera.position.z - this.person.position.z,
    );

    this.renderer.render(this.scene, this.camera);
  }

  dispose(): void {
    this.stop();
    if (this.canvas) {
      this.canvas.removeEventListener('pointerdown', this.onPointerDown);
      this.canvas.removeEventListener('pointermove', this.onPointerMove);
      this.canvas.removeEventListener('pointerup', this.onPointerUp);
      this.canvas.removeEventListener('pointercancel', this.onPointerUp);
      this.canvas.removeEventListener('wheel', this.onWheel);
      this.canvas.removeEventListener('dblclick', this.onDoubleClick);
    }
    this.clearPlant();
    if (this.grid) {
      this.grid.geometry.dispose();
      disposeMaterial(this.grid.material);
      this.grid = null;
    }
    this.ground.geometry.dispose();
    disposeMaterial(this.ground.material);
    this.person.geometry.dispose();
    const personMaterial = this.person.material as THREE.MeshBasicMaterial;
    personMaterial.map?.dispose();
    disposeMaterial(personMaterial);
    this.backgroundTexture?.dispose();
    this.backgroundTexture = null;
    this.renderer?.dispose();
  }
}

/* --------------------------------------- Helpers --------------------------------------- */

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value));
}

function now(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

function disposeMaterial(material: THREE.Material | THREE.Material[]): void {
  if (Array.isArray(material)) material.forEach((m) => m.dispose());
  else material.dispose();
}

/** Two-stop vertical wash, used as the studio backdrop. */
function gradientTexture(stops: [string, string]): THREE.Texture {
  const canvas = document.createElement('canvas');
  canvas.width = 4;
  canvas.height = 64;
  const context = canvas.getContext('2d');
  if (context) {
    const gradient = context.createLinearGradient(0, 0, 0, canvas.height);
    gradient.addColorStop(0, stops[0]);
    gradient.addColorStop(1, stops[1]);
    context.fillStyle = gradient;
    context.fillRect(0, 0, canvas.width, canvas.height);
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

/**
 * Draw the cutout: a plain standing figure, filled flat and slightly translucent so it never
 * competes with the plant it is there to measure.
 */
function personTexture(): THREE.CanvasTexture {
  // 360 rows for 180 cm, so the drawing is dimensioned in half-centimetres and the figure fills the
  // texture exactly top to bottom — any transparent margin would read as a person hovering.
  const width = 128;
  const height = 360;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');

  if (context) {
    context.clearRect(0, 0, width, height);
    // White and opaque: the material's colour tints the figure per theme, and the alpha channel has
    // to stay a clean in/out mask for the shadow pass's threshold to trace the silhouette.
    context.fillStyle = '#ffffff';

    const midX = width / 2;
    // Head: 23 cm crown to chin, its top exactly at the texture's top edge.
    context.beginPath();
    context.arc(midX, 23, 23, 0, Math.PI * 2);
    context.fill();

    // One closed outline for neck, shoulders, arms, torso and legs — drawn down the left side and
    // back up the right, so the figure stays symmetrical. Feet land on the bottom edge.
    context.beginPath();
    context.moveTo(midX - 10, 44);
    context.lineTo(midX - 13, 58);
    context.quadraticCurveTo(midX - 40, 66, midX - 44, 96);
    context.lineTo(midX - 47, 186);
    context.quadraticCurveTo(midX - 46, 200, midX - 32, 198);
    context.lineTo(midX - 30, 120);
    context.lineTo(midX - 31, 196);
    context.lineTo(midX - 29, 360);
    context.lineTo(midX - 5, 360);
    context.lineTo(midX - 2, 208);
    context.lineTo(midX + 2, 208);
    context.lineTo(midX + 5, 360);
    context.lineTo(midX + 29, 360);
    context.lineTo(midX + 31, 196);
    context.lineTo(midX + 30, 120);
    context.lineTo(midX + 32, 198);
    context.quadraticCurveTo(midX + 46, 200, midX + 47, 186);
    context.lineTo(midX + 44, 96);
    context.quadraticCurveTo(midX + 40, 66, midX + 13, 58);
    context.lineTo(midX + 10, 44);
    context.closePath();
    context.fill();
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 4;
  return texture;
}
